<template>
  <div>
    <base-submit-info-modal ref="mailerFormModal"
                            :confirm-function="showCloseConfirmationModal"
                            cancel-button-variant="warning"
                            confirm-button-title="Send mails"
                            confirm-button-variant="info"
                            modal-title="Group mails form">
      <base-submit-danger-modal ref="closeConfirmationModal"
                                :confirm-function="submitSendingGroupMails"
                                confirm-button-title="Confirm"
                                modal-title="Are you sure?">
        <p>
          Clicking "Confirm" will result in sending {{ this.activeFilteredEmailAddresses.length }} mails to main authors
          + copies will be sent to technical roles (if you choose this option)
        </p>
      </base-submit-danger-modal>
      <b-alert show
               size="lg"
               variant="dark">

        <b>1) Button "Send mails" sends mails to the main authors of previously filtered proposals</b><br>
        <b>2) Tags {} are required to dynamically fill form. Make sure NOT to modify content in the tags</b><br>
        <b>3) Description of "context" tags {} <a href="https://wiki.synchrotron.pl/wiki/Proposal" target="_blank">
          Link
        </a></b><br>
        <b>4) For each proposal an email will also be sent to the users with role: "technical"
          (for the end station of proposal) for synchrotron proposals or "microscope_cryo_technicalManager"
          for cryo-EM proposals (if this option was selected in the form).
        </b><br>

        <b v-if="isProposalsSubmittedState">
          5) Make sure that proposals are submitted
        </b>
      </b-alert>
      <b-form-group label-class="mt-2 font-weight-bold">
        <b-tabs content-class="mt-3">

          <b-tab active title="Message Editor">
            <b-form-group label-class="mt-2 font-weight-bold">
              <b-form-group>
                <p class="mt-2 font-weight-bold">Mail title</p>
                <div class="d-flex align-items-center justify-content-between">
                  <b-form-input
                    v-model="mailTitle"
                    class="mr-3"
                    placeholder="Mail title"
                    style="max-width: 700px;">
                  </b-form-input>

                  <b-form-checkbox v-model="sendToTechnical">
                    Send also to technical users
                  </b-form-checkbox>
                </div>
              </b-form-group>
              <p class="mt-2 font-weight-bold">Mail Content</p>
              <b-form-textarea
                id="comment"
                v-model="renderedMailContent"
                :placeholder="placeholder"
                rows="24"/>
            </b-form-group>

            <b-modal
              id="preview-modal"
              ref="previewModal"
              :title="mailTitle"
              hide-footer size="xl">
              <p class="mt-2">Dear &lt;username&gt;</p>

              <div v-html="previewContent"></div>
              <br>
              <p class="font-weight-bold">Best regards,</p>
              <p class="font-weight-bold">SOLARIS User Office</p>
              <p>This message was sent automatically, please do not reply.</p>

              <div class="text-right mt-3">
                <b-button variant="secondary" @click="$refs.previewModal.hide()">
                  Close
                </b-button>
              </div>
            </b-modal>

            <div>
              <b-button class="mr-3" variant="info" @click="showPreview">
                Preview of mail content
              </b-button>
            </div>
          </b-tab>

          <b-tab title="Recipients">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <b-form-input
                v-model="emailFilter"
                class="mr-3"
                placeholder="Filter emails..."/>
            </div>

            <div class="email-list-container">
              <b-form-checkbox
                v-for="email in filteredEmails"
                :key="email.proposal_id"
                v-model="email.active"
                class="mb-2"
              >
                {{ email.address }}
              </b-form-checkbox>
            </div>
            <div class="mt-2">
              {{ activeFilteredEmailAddresses.length }} mail addresses are ready to be sent
            </div>
          </b-tab>
          <hr class="centered-divider">
          <loading-progress-bar
            :current-step="sendingCurrentStep"
            :total-steps="activeFilteredEmailAddresses.length"
            :visible="isSendingMails"
            label="Sending group mails"/>
        </b-tabs>
      </b-form-group>
    </base-submit-info-modal>
    <mail-sending-summary-modal
      ref="sendingSummaryModal"
      :failed-recipients="failedRecipients"
      :sent-recipients="sentRecipients"
      @close-parent="closeMailerForm"/>
  </div>
</template>

<script>
  import BaseSubmitInfoModal from '@/components/base-components/base-modals/BaseSubmitInfoModal';
  import BaseSubmitDangerModal from '@/components/base-components/base-modals/BaseSubmitDangerModal';
  import LoadingProgressBar from '@/components/mailer/LoadingProgressBar';
  import MailSendingSummaryModal from '@/components/mailer/MailSendingSummaryModal';

  export default {
    name: 'BasicMailerForm',
    components: { BaseSubmitInfoModal, BaseSubmitDangerModal, LoadingProgressBar, MailSendingSummaryModal },
    props: {
      isProposalsSubmittedState: {
        type: Boolean,
        required: true,
      },
      mailContent: {
        type: String,
        required: true,
      },
      filterQuery: { required: true },
      usersMailingList: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        placeholder: 'Please note that the input provided in this field constitutes'
          + ' the content of the emails that will be sent to users',
        renderedMailContent: '',
        submitTrigger: false,
        previewContent: '',
        emailFilter: '',
        mailTitle: 'Proposal grade',
        sendToTechnical: true,
        mailingList: [],
        isSendingMails: false,
        sendingCurrentStep: 0,
        sentRecipients: [],
        failedRecipients: [],
      };
    },
    methods: {
      showPreview() {
        this.previewContent = this.renderMailContentToSend(
          this.renderedMailContent,
        );

        this.$refs.previewModal.show();
      },
      showCloseConfirmationModal() {
        if (!this.isSendingMails) {
          this.$refs.closeConfirmationModal.show();
        }
      },
      renderMailContentToModal(mailContent) {
        return mailContent.replaceAll('<p>', '')
          .replaceAll('</p>', '');
      },
      renderMailContentToSend(mailContent) {
        return `<p>${mailContent.replaceAll('\n', '</p><p>')}</p>`;
      },
      async submitSendingGroupMails() {
        try {
          this.$refs.closeConfirmationModal.hide();
          const mailContent = this.renderMailContentToSend(this.renderedMailContent);
          const sleep = (ms) => new Promise((resolve) => {
            setTimeout(resolve, ms);
          });
          this.isSendingMails = true;
          this.sendingCurrentStep = 0;

          await this.activeFilteredEmailAddresses.reduce(
            (chain, email) => chain.then(() => sleep(5000)).then(() => this.axios
              .post(
                '/mails/single/proposals-grades',
                {
                  /* eslint-disable camelcase */
                  mail_title: this.mailTitle,
                  mail_content: mailContent,
                  send_to_technicals: this.sendToTechnical,
                  main_author_mail: email.address,
                  proposal_id: email.proposal_id,
                  /* eslint-enable camelcase */
                },
                { params: this.filterQuery },
              )
              .then((response) => {
                const sent = response?.data?.is_sent === true;
                this[sent ? 'sentRecipients' : 'failedRecipients'].push(email);
                this.$notify({
                  type: sent ? 'success' : 'error',
                  title: sent
                    ? `Mail sent to ${email.address}`
                    : `Failed to send mail to ${email.address}`,
                });
              })
              .catch(() => {
                this.failedRecipients.push(email);
                this.$notify({
                  type: 'error',
                  title: `Error sending mail to ${email}`,
                });
              })
              .finally(() => {
                this.sendingCurrentStep += 1;
              })),
            Promise.resolve(),
          );
          this.$refs.mailerFormModal.hide();

          this.$nextTick(() => {
            this.$refs.sendingSummaryModal.show();
          });
        } catch (error) {
          this.$notify({ title: 'Error occurred while sending mail', type: 'error' });
          window.location.reload();
        } finally {
          this.isSendingMails = false;
        }
      },
      openModal() {
        this.$refs.mailerFormModal.show();
        this.renderedMailContent = this.renderMailContentToModal(this.mailContent);
      },
      closeMailerForm() {
        this.sentRecipients = [];
        this.failedRecipients = [];
        this.$refs.mailerFormModal.hide();
      },
    },
    computed: {
      filteredEmails() {
        if (!this.emailFilter) return this.mailingList;

        const filter = this.emailFilter.toLowerCase();

        return this.mailingList.filter((email) => email.address.toLowerCase().includes(filter));
      },
      activeFilteredEmailAddresses() {
        return this.filteredEmails.filter((email) => email.active);
      },
    },
    watch: {
      usersMailingList: {
        handler(newVal) {
          this.mailingList = newVal.map((item) => ({
            ...item,
            active: Object.prototype.hasOwnProperty.call(item, 'active')
              ? item.active
              : true,
          }));
        },
        immediate: true,
        deep: false,
      },
    },
  };
</script>

<style scoped>
  .email-list-container {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #ddd;
    padding: 10px;
    border-radius: 4px;
  }

  .centered-divider {
    margin: 15px auto;
    border-top: 1px solid #ccc;
  }
</style>
